import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { BASE_URI } from '../globals';

const AuthContext = createContext();

const TOKEN_KEY = 'auth_token';

export function AuthProvider({ children }) {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const isAuthenticated = !!token;

    const login = useCallback(async (username, password) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${BASE_URI}/token/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ username, password })
            });

            if (!response.ok) {
                if (response.status === 400) {
                    throw new Error('Ongeldige gebruikersnaam of wachtwoord');
                }
                throw new Error('Er is een fout opgetreden bij het inloggen');
            }

            const data = await response.json();
            localStorage.setItem(TOKEN_KEY, data.token);
            setToken(data.token);
            return true;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setError(null);
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Verify token is still valid on mount
    useEffect(() => {
        if (token) {
            fetch(`${BASE_URI}/user/current/`, {
                headers: {
                    Authorization: `Token ${token}`
                }
            }).catch(() => {
                // Token is invalid, clear it
                logout();
            });
        }
    }, [token, logout]);

    const providerParams = useMemo(
        () => ({
            token,
            isAuthenticated,
            isLoading,
            error,
            login,
            logout,
            clearError
        }),
        [token, isAuthenticated, isLoading, error, login, logout, clearError]
    );

    return <AuthContext.Provider value={providerParams}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
